
/* ==========================================================================
   UI INTERACTIONS / PINNING / DRAWERS / EVENT DELEGATION
   (/database/js/ui-interactions.js)
   ========================================================================== */

import { state, setCaseModeSelection, clearAllSelectedComponents } from '../state.js?v=20260819-audit2';
import { getCaseBySlug } from './runtime-index.js?v=20260819-audit2';
import { clearCardPool, cleanupOrphanNoteModals, closeSpecNoteModal, updateSliderBadges } from '../ui-core.js?v=20260819-audit2';
import { activateAccessibleLayer, deactivateAccessibleLayer } from '../a11y.js?v=20260819-audit2';
import { escapeHtml, formatCaseBrandTitle, slugifyUrlSegment, caseRouteSlug, AIO_COMBOBOX_IDS, getComboboxInputValue } from '../utils.js?v=20260819-audit2';
import { isSingleCaseUrlView, updateExplorerUiTitle, bridgeCaseTo3DWorkbench, togglePinCase, buildHybridShareUrl, renderSingleCaseBreadcrumbs } from '../seo.js?v=20260822-audit3';
import { GalleryModal } from './gallery-modal.js?v=20260819-phase35';
import { toggleBuildsDrawer, handleCardThumbnailClick, handleCardNavClick, handleLoadBuildClick } from './builds-drawer.js?v=20260819-phase35';
import { toggleReviewsDrawer } from './reviews-drawer.js?v=20260819-phase35';
import { generateTechnicalSpecsDrawerHtml } from './ui-helpers.js?v=20260819-audit2';
import { invalidateActiveReport, triggerReportGeneration } from '../reports.js?v=20260822-popular2';
import { logCurrentBuildActivity } from '../telemetry.js?v=20260822-popular2';

export function renderPinDock(pinnedIndices, casesGlobal, onUnpinCallback) {
  const tray = document.getElementById('sffPinTray') || document.getElementById('sffPinDock');
  if (!tray) return;

  const count = Array.isArray(pinnedIndices) ? pinnedIndices.length : 0;
  const countNode = document.getElementById('sffPinDockCount');
  if (countNode) countNode.textContent = `${count}/2`;
  const details = document.getElementById('sffPinDockDetails');
  if (details) {
    const previous = Number(details.dataset.lastPinCount || 0);
    if (count > 0 && previous === 0) details.open = true;
    details.dataset.lastPinCount = String(count);
  }

  if (!pinnedIndices || pinnedIndices.length === 0) {
    tray.innerHTML = `<div class="pin-empty-notice font-mono" style="font-size:10px;color:var(--sff-text-muted);padding:4px 0;">Pin up to 2 cases to enable side-by-side comparison mode.</div>`;
    return;
  }

  const itemsHtml = pinnedIndices.map(idx => {
    const c = casesGlobal[idx];
    if (!c) return '';
    return `
      <div style="display:flex;justify-content:space-between;align-items:center;background:var(--sff-bg-panel);border:1px solid var(--sff-border);padding:4px 8px;border-radius:2px;margin-bottom:4px;font-size:10px;" class="font-mono">
        <span style="font-weight:700;color:var(--sff-text-dark);">${escapeHtml(formatCaseBrandTitle(c.name))}</span>
        <button type="button" class="sff-unpin-btn" data-unpin-idx="${idx}" style="background:none;border:none;color:var(--sff-rose);cursor:pointer;font-weight:800;font-size:12px;line-height:1;">×</button>
      </div>`;
  }).join('');

  const compareTriggerHtml = pinnedIndices.length === 2 ? `
    <button type="button" id="sffTriggerCompareBtn" style="width:100%;margin-top:6px;height:28px;background:var(--sff-sky);color:#000000;border:none;border-radius:2px;font-size:9.5px;font-weight:800;cursor:pointer;" class="font-mono">
      COMPARE PINNED CASES (2/2) ↗
    </button>` : '';

  tray.innerHTML = itemsHtml + compareTriggerHtml;

  tray.querySelectorAll('.sff-unpin-btn').forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const idx = parseInt(btn.getAttribute('data-unpin-idx'), 10);
      if (onUnpinCallback) onUnpinCallback(idx);
    });
  });

  const compareBtn = document.getElementById('sffTriggerCompareBtn');
  if (compareBtn) {
    compareBtn.addEventListener('click', () => {
      const cA = casesGlobal[pinnedIndices[0]];
      const cB = casesGlobal[pinnedIndices[1]];
      if (cA && cB) {
        const slugA = caseRouteSlug(cA);
        const slugB = caseRouteSlug(cB);
        window.location.href = `/db/compare/${slugA}-vs-${slugB}/`;
      }
    });
  }
}

export function initControlBarListeners(dom, evaluateCompatibility) {
  const btnAllCases = document.getElementById('btnAllCases');
  const customToggles = document.querySelectorAll('#combinedScopeGroup .toggle-modifier');

  // Single-case breadcrumb DATABASE link is an in-app "back to feed" action.
  // It must not behave like a fresh /db/ page load because that destroys the
  // active compatibility configuration and pinned-case context.
  if (!document.documentElement.dataset.sffBreadcrumbFeedBound) {
    document.documentElement.dataset.sffBreadcrumbFeedBound = '1';
    document.addEventListener('click', (event) => {
      const backToFeed = event.target.closest?.('#sffCaseBreadcrumb [data-sff-back-to-feed="true"]');
      if (!backToFeed) return;

      event.preventDefault();

      // Leave hardware, dimensions, modes, filters, pins and evidence state untouched.
      // Only leave the single-case route/view and clear its case-search constraint.
      state.selectedCaseIdx = null;
      state.userExplicitUnselect = true;
      state.initialShortcodeUrl = null;
      state.lastHydratedCaseSlug = null;
      window._initialShortcodeUrl = null;
      window._lastHydratedCaseSlug = null;
      state.routeNavigationIntent = 'replace';

      if (dom.search) dom.search.value = '';
      const caseInput = document.querySelector('#caseSearchCombobox .combobox-input');
      if (caseInput) {
        caseInput.value = '';
        const clearBtn = caseInput.parentElement?.querySelector('.combobox-clear-btn');
        if (clearBtn) clearBtn.style.display = 'none';
      }

      // Push the feed route so browser Back can still return to the case page,
      // but do not reload the document. evaluateCompatibility() then rerenders
      // the feed against the exact compatibility state already in memory.
      if (location.pathname !== '/db/' || location.search || location.hash) {
        history.pushState({ sffView: 'feed' }, '', '/db/');
      }

      clearCardPool();
      cleanupOrphanNoteModals();
      renderSingleCaseBreadcrumbs('');
      evaluateCompatibility(true);
    });
  }
  
  if (btnAllCases) {
    btnAllCases.addEventListener('click', () => { 
      btnAllCases.classList.add('active'); 
      btnAllCases.setAttribute('aria-pressed', 'true'); 
      customToggles.forEach(b => { 
        b.classList.remove('active'); 
        b.setAttribute('aria-pressed', 'false'); 
        const prop = b.getAttribute('data-prop');
        if (prop) state.filterFlags[prop] = false; 
      }); 
      evaluateCompatibility(); 
    });
  }

  customToggles.forEach(b => b.addEventListener('click', () => { 
    const p = b.getAttribute('data-prop'); 
    if (p) {
      state.filterFlags[p] = !state.filterFlags[p]; 
      b.classList.toggle('active', state.filterFlags[p]); 
      b.setAttribute('aria-pressed', String(state.filterFlags[p])); 
      if (Object.values(state.filterFlags).some(v => v === true)) { 
        if (btnAllCases) { btnAllCases.classList.remove('active'); btnAllCases.setAttribute('aria-pressed', 'false'); } 
      } else { 
        if (btnAllCases) { btnAllCases.classList.add('active'); btnAllCases.setAttribute('aria-pressed', 'true'); } 
      } 
      evaluateCompatibility(); 
    }
  }));

  const clearBtn = document.getElementById('clearDefaultsBtn');
  if (clearBtn) {
    clearBtn.addEventListener('click', () => {
      state.sffPinnedCases = [];
      window.sffPinnedCases = [];
      state.initialShortcodeUrl = null;
      state.lastHydratedCaseSlug = null;
      window._initialShortcodeUrl = null;
      window._lastHydratedCaseSlug = null;
      state.isRestoringShortcodeState = false;
      window._isRestoringShortcodeState = false;
      clearCardPool();
      cleanupOrphanNoteModals();
      
      state.selectedCaseIdx = null;
      state.userExplicitUnselect = true;
      state.activeLayout = 'all';

      clearAllSelectedComponents();
      state.portableBuildHardware = { psu: null, ram: null };
      state.portableFixedStep = null;

      invalidateActiveReport();

      if (document.getElementById('coolingMode')) document.getElementById('coolingMode').value = 'air';
      if (document.getElementById('moboType')) document.getElementById('moboType').value = 'ITX';
      if (document.getElementById('psuType')) document.getElementById('psuType').value = 'SFX';
      if (document.getElementById('radiatorSize')) document.getElementById('radiatorSize').value = '240';
      if (document.getElementById('radFanType')) document.getElementById('radFanType').value = 'standard';
      if (document.getElementById('gpuPcieSlots')) document.getElementById('gpuPcieSlots').value = '2';
      if (document.getElementById('ramHeight')) document.getElementById('ramHeight').value = '35';
      if (document.getElementById('ramModules')) document.getElementById('ramModules').value = '2';

      if (dom.search) dom.search.value = "";
      document.querySelectorAll('#gpuCombobox .combobox-input, #cpuCoolerCombobox .combobox-input, #aioCoolerCombobox .combobox-input, #aioSearchCombobox .combobox-input, #aioCombobox .combobox-input, #moboCombobox .combobox-input, #ramCombobox .combobox-input, #caseSearchCombobox .combobox-input').forEach(i => {
        i.value = '';
        const cb = i.parentElement?.querySelector('.combobox-clear-btn');
        if (cb) cb.style.display = 'none';
      });

      const airP = document.getElementById('airCoolingPanel');
      const liqP = document.getElementById('liquidCoolingPanel');
      if (airP) airP.classList.add('active');
      if (liqP) liqP.classList.remove('active');

      if (location.pathname !== '/db/' && location.pathname !== '/db') {
        history.replaceState(null, '', '/db/');
        document.title = "SFFLABS - DATABASE";
        updateExplorerUiTitle("COMPATIBILITY EXPLORER");
        renderSingleCaseBreadcrumbs('');
      }

      document.querySelectorAll('.segment-btn').forEach(b => {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
        b.setAttribute('aria-checked', 'false');
      });

      document.querySelector('#moboSegmented .segment-btn[data-value="ITX"]')?.classList.add('active');
      document.querySelector('#psuSegmented .segment-btn[data-value="SFX"]')?.classList.add('active');
      document.querySelector('#coolingModeSegmented .segment-btn[data-value="air"]')?.classList.add('active');
      document.querySelector('#radiatorSizeSegmented .segment-btn[data-value="240"]')?.classList.add('active');
      document.querySelector('#radFanSegmented .segment-btn[data-value="standard"]')?.classList.add('active');
      document.querySelector('#gpuPcieSegmented .segment-btn[data-value="2"]')?.classList.add('active');
      document.querySelector('#ramModuleSegmented .segment-btn[data-value="2"]')?.classList.add('active');
      document.querySelector('#layoutFilterGroup .segment-btn[data-filter="all"]')?.classList.add('active');
      
      const btnAllCases = document.getElementById('btnAllCases');
      if (btnAllCases) {
        btnAllCases.classList.add('active');
        btnAllCases.setAttribute('aria-pressed', 'true');
      }
      document.querySelectorAll('#combinedScopeGroup .toggle-modifier').forEach(b => {
        b.classList.remove('active');
        b.setAttribute('aria-pressed', 'false');
        const prop = b.getAttribute('data-prop');
        if (prop) state.filterFlags[prop] = false;
      });

      if (dom.gpuL) dom.gpuL.value = 304; 
      if (dom.gpuT) dom.gpuT.value = 40; 
      if (dom.gpuH) dom.gpuH.value = 137; 
      if (dom.cpuH) dom.cpuH.value = 47;
      if (dom.ramH) dom.ramH.value = 35;
      if (dom.ramModules) dom.ramModules.value = 2;
      if (dom.vol) dom.vol.value = 10; 
      if (dom.sortSelector) dom.sortSelector.value = "vol-high"; 

      const feed = document.getElementById('caseList');
      if (feed) feed.classList.remove('side-by-side-comparison');

      updateSliderBadges(); 
      evaluateCompatibility();
    });
  }

  if (dom.shareBtn) {
    dom.shareBtn.addEventListener('click', async (e) => {
      e.preventDefault();
      const gpuQuery = document.querySelector('#gpuCombobox .combobox-input')?.value.trim() || '';
      const cpuQuery = document.querySelector('#cpuCoolerCombobox .combobox-input')?.value.trim() || '';
      const aioQuery = getComboboxInputValue(AIO_COMBOBOX_IDS);
      const moboQuery = document.querySelector('#moboCombobox .combobox-input')?.value.trim() || '';
      const ramQuery = document.querySelector('#ramCombobox .combobox-input')?.value.trim() || '';

      const specs = {
        mobo: dom.moboVal ? dom.moboVal.value : 'ITX',
        psu: dom.psuVal ? dom.psuVal.value : 'SFX',
        vol: dom.vol ? dom.vol.value : '10',
        gpuL: dom.gpuL ? dom.gpuL.value : '304',
        gpuT: dom.gpuT ? dom.gpuT.value : '40',
        gpuH: dom.gpuH ? dom.gpuH.value : '137',
        pcie: dom.gpuPcie ? dom.gpuPcie.value : '2',
        cool: dom.cooling ? dom.cooling.value : 'air',
        cpuH: dom.cpuH ? dom.cpuH.value : '47',
        rad: dom.radSize ? dom.radSize.value : '240',
        fan: dom.radFanVal ? dom.radFanVal.value : 'standard',
        ramH: dom.ramH ? dom.ramH.value : '35',
        ramModules: dom.ramModules ? dom.ramModules.value : '2'
      };

      if (dom.search && dom.search.value.trim()) specs.search = dom.search.value.trim();
      if (gpuQuery) specs.gpuSearch = gpuQuery;
      if (cpuQuery) specs.cpuSearch = cpuQuery;
      if (aioQuery) specs.aioSearch = aioQuery;
      if (moboQuery) specs.moboSearch = moboQuery;
      if (ramQuery) specs.ramSearch = ramQuery;

      let activeCaseName = null;

      if (state.selectedCaseIdx !== null && state.fetchedCasesGlobal[state.selectedCaseIdx]) {
        activeCaseName = state.fetchedCasesGlobal[state.selectedCaseIdx].name;
      } else if (state.lastHydratedCaseSlug) {
        const matched = getCaseBySlug(state.lastHydratedCaseSlug);
        activeCaseName = matched ? matched.name : state.lastHydratedCaseSlug;
      } else if (isSingleCaseUrlView()) {
        const pathSegments = location.pathname.split('/').filter(Boolean);
        const dbIdx = pathSegments.indexOf('db');
        if (dbIdx !== -1 && pathSegments.length > dbIdx + 1) {
          activeCaseName = pathSegments[dbIdx + 1];
        }
      } else if (dom.search && dom.search.value.trim()) {
        activeCaseName = dom.search.value.trim();
      }

      const shareUrl = buildHybridShareUrl(activeCaseName, specs);

      try {
        let copied = false;
        if (navigator.clipboard && navigator.clipboard.writeText) {
          try {
            await navigator.clipboard.writeText(shareUrl);
            copied = true;
          } catch (clipErr) { console.warn('Clipboard write failed', clipErr); }
        }

        if (!copied) {
          const ta = document.createElement('textarea');
          ta.value = shareUrl;
          ta.style.position = 'fixed';
          ta.style.opacity = '0';
          document.body.appendChild(ta);
          ta.select();
          copied = document.execCommand('copy');
          document.body.removeChild(ta);
        }

        const textNode = document.getElementById('shareBtnText');
        if (textNode) {
          const originalText = textNode.innerText;
          textNode.innerText = 'COPIED';
          setTimeout(() => textNode.innerText = originalText, 2000);
        }
        if (copied) logCurrentBuildActivity('share');
      } catch (err) { console.error('Share URL copy failed:', err); }
    });
  }
}

export function initCardFeedDelegation(dom, evaluateCompatibility) {
  if (!dom.list) return;

  dom.list.addEventListener('change', (e) => {
    const modeSelect = e.target.closest('.sff-mode-select');
    if (modeSelect) {
      const caseName = modeSelect.getAttribute('data-case-name');
      const caseIdx = parseInt(modeSelect.getAttribute('data-case-idx'), 10);
      const selectedModeId = modeSelect.value;

      if (caseName) {
        const activeCase = state.fetchedCasesGlobal[caseIdx];
        if (activeCase) setCaseModeSelection(activeCase, selectedModeId);
        state.selectedModeId = selectedModeId;
        // A forced/imported spine step belongs to the previous mode. Switching modes
        // returns the case to that mode's normal deterministic spine resolution.
        state.portableFixedStep = null;
        if (activeCase && Array.isArray(activeCase.modes)) {
          // A mode switch changes the CASE capability envelope, not the user's build.
          // Preserve exact CPU/GPU/AIO identity and all user-entered dimensions, then
          // re-evaluate the same hardware against the newly selected mode.
          // Case navigation remains intentionally different: applyCaseCompatibleDefaults()
          // still clears previous hardware when moving to a different case/page/URL.
          updateSliderBadges();
        }

        invalidateActiveReport();
        evaluateCompatibility();
      }
    }
  });

  dom.list.addEventListener('click', async (e) => {
    // 0. CASE TITLE / THUMBNAIL CLICK TO NAVIGATE TO SINGLE CASE PAGE
    const navLink = e.target.closest('.ws-case-nav-link');
    if (navLink) {
      // Real anchors keep native browser behavior (Ctrl/Cmd click, middle-click,
      // copy link, keyboard activation). Legacy non-anchor nav hooks still work.
      if (navLink.matches('a[href]')) return;
      e.preventDefault();
      e.stopPropagation();
      const slug = navLink.getAttribute('data-slug');
      if (slug) window.location.href = `/db/${slug}/`;
      return;
    }

    // 1. GALLERY LAYER BUTTON HANDLER
    const galleryBtn = e.target.closest('.sff-gallery-layer-btn');
    if (galleryBtn) {
      e.preventDefault();
      e.stopPropagation();
      const caseIdx = parseInt(galleryBtn.getAttribute('data-idx'), 10);
      const caseObj = state.fetchedCasesGlobal[caseIdx];
      if (caseObj) {
        GalleryModal.open(caseObj);
      }
      return;
    }

    // 2. EDITORIAL REVIEWS LAYER BUTTON HANDLER
    const reviewsBtn = e.target.closest('.sff-reviews-layer-btn');
    if (reviewsBtn) {
      e.preventDefault();
      e.stopPropagation();
      toggleReviewsDrawer(reviewsBtn);
      return;
    }

    // 3. COMMUNITY BUILDS LAYER BUTTON HANDLER
    const buildsBtn = e.target.closest('.sff-community-builds-toggle-btn');
    if (buildsBtn) {
      e.preventDefault();
      e.stopPropagation();
      toggleBuildsDrawer(buildsBtn);
      return;
    }

    // 4. LOAD A DOCUMENTED COMMUNITY BUILD INTO THE CONFIGURATOR
    const loadBuildBtn = e.target.closest('.community-load-config-btn');
    if (loadBuildBtn) {
      e.preventDefault();
      e.stopPropagation();
      await handleLoadBuildClick(loadBuildBtn);
      return;
    }

    // 5. MINI CARD GALLERY THUMBNAIL CLICK
    const thumbDot = e.target.closest('.community-thumb-dot');
    if (thumbDot) {
      e.preventDefault();
      e.stopPropagation();
      handleCardThumbnailClick(thumbDot);
      return;
    }

    // 6. MINI CARD GALLERY NAV CLICK
    const navBtn = e.target.closest('.community-card-nav');
    if (navBtn) {
      e.preventDefault();
      e.stopPropagation();
      handleCardNavClick(navBtn);
      return;
    }

    // 7. PIN CARD BUTTON HANDLER
    const pinBtn = e.target.closest('.sff-pin-card-btn');
    if (pinBtn) {
      const caseIdx = parseInt(pinBtn.getAttribute('data-pin-idx'), 10);
      togglePinCase(caseIdx, evaluateCompatibility);
      return;
    }

    // 8. VALIDATE CARD BUTTON HANDLER
    const validateBtn = e.target.closest('.sff-validate-card-btn');
    if (validateBtn) {
      if (validateBtn.disabled || validateBtn.classList.contains('disabled-btn')) return;

      const caseIdx = parseInt(validateBtn.getAttribute('data-idx'), 10);
      const isMobile = window.innerWidth <= 1024;

      if (isMobile && state.selectedCaseIdx === caseIdx) {
        triggerReportGeneration(dom, validateBtn);
        return;
      }

      if (state.selectedCaseIdx === caseIdx) {
        state.selectedCaseIdx = null;
        state.userExplicitUnselect = true;
      } else {
        state.selectedCaseIdx = caseIdx;
        state.userExplicitUnselect = false;
      }

      evaluateCompatibility(true);
      return;
    }

    // 8. SPEC MICRO NOTES MODAL TOGGLE
    const toggle = e.target.closest('.spec-micro-toggle'); 
    if (toggle) { 
      const boxId = toggle.getAttribute('data-target'); 
      let targetBox = document.getElementById(boxId); 
      if (!targetBox) { 
        const noteKey = toggle.getAttribute('data-key'); 
        const caseIdx = toggle.getAttribute('data-idx'); 
        const c = state.fetchedCasesGlobal[caseIdx]; 
        if (!c) return; 

        targetBox = document.createElement('div'); 
        targetBox.className = 'spec-dropdown-box font-mono'; 
        targetBox.id = boxId; 
        targetBox.setAttribute('aria-hidden', 'true');

        let contentHtml = noteKey === 'gpu-combined'
          ? [c.specNotes?.gpu, c.specNotes?.thick, c.specNotes?.height].filter(Boolean).map(escapeHtml).join('<hr style="border:0;border-top:1px dashed var(--sff-border);margin:6px 0;">')
          : escapeHtml(c.specNotes?.[noteKey] || "");

        const titleId = `${boxId}-title`;
        targetBox.setAttribute('aria-labelledby', titleId);
        targetBox.innerHTML = `<div class="spec-modal-header"><span class="spec-modal-title" id="${titleId}">NOTES</span><button type="button" class="spec-modal-close" data-target="${boxId}" aria-label="Close notes">×</button></div><div class="spec-modal-content">${contentHtml}</div>`; 
        document.body.appendChild(targetBox);
      } 

      toggle.setAttribute('aria-controls', boxId);
      toggle.setAttribute('aria-haspopup', 'dialog');
      const isVisible = targetBox.classList.contains('active'); 
      document.querySelectorAll('.spec-dropdown-box.active').forEach(box => { 
        if (box !== targetBox) closeSpecNoteModal(box, { restoreFocus: false });
      }); 

      if (isVisible) {
        closeSpecNoteModal(targetBox);
      } else {
        targetBox.classList.add('active');
        toggle.innerText = "NOTES ▲";
        toggle.setAttribute('aria-expanded', 'true');
        activateAccessibleLayer(targetBox, {
          label: 'Specification notes',
          initialFocusSelector: '.spec-modal-close',
          onClose: () => closeSpecNoteModal(targetBox)
        });
      }
      return; 
    }

    // 9. 3D WORKBENCH HOOK
    const workbenchBtn = e.target.closest('.sff-3d-hook-btn');
    if (workbenchBtn) { 
      const caseName = workbenchBtn.getAttribute('data-case-name'); 
      if (caseName) bridgeCaseTo3DWorkbench(caseName); 
      return; 
    }
    
    // 10. EXTRA TECHNICAL SPECS DRAWER
    const drawerBtn = e.target.closest('.extra-specs-toggle-btn'); 
    if (drawerBtn && drawerBtn.id !== 'loadMoreBtn') { 
      const card = drawerBtn.closest('.case-card');
      const targetDrawer = card ? card.querySelector('.extra-specs-drawer') : document.getElementById(drawerBtn.getAttribute('data-target')); 
      if (targetDrawer && targetDrawer.innerHTML.trim() === "") { 
        const caseIdx = drawerBtn.getAttribute('data-idx'); 
        const c = state.fetchedCasesGlobal[caseIdx]; 
        if (!c) return; 
        targetDrawer.innerHTML = generateTechnicalSpecsDrawerHtml(c, caseIdx);
      } 
      if (targetDrawer) { 
        const isOpen = targetDrawer.classList.contains('active'); 
        targetDrawer.classList.toggle('active', !isOpen);
        targetDrawer.setAttribute('aria-hidden', isOpen ? 'true' : 'false');
        drawerBtn.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
        drawerBtn.innerText = !isOpen ? "HIDE TECHNICAL SPECIFICATIONS ▲" : "VIEW TECHNICAL SPECIFICATIONS ▼"; 
      }
      return;
    }

    // 11. GENERAL LIVE-FEED CARD NAVIGATION
    // Only cards explicitly marked by their renderer participate. Single-case and
    // comparison cards are deliberately excluded to reduce accidental navigation.
    const feedCard = e.target.closest('.case-card[data-card-nav-slug]');
    if (!feedCard || e.defaultPrevented) return;
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    // Interactive regions always win over the broad card hit area. This keeps
    // buttons, selects, notes, external links and future controls safe by default.
    if (e.target.closest('button, a, input, select, textarea, label, summary, [role="button"], [contenteditable="true"], [data-no-card-nav], .spec-micro-toggle, .spec-dropdown-box, .extra-specs-drawer, .ws-community-builds-drawer, .ws-reviews-drawer')) return;

    // Don't navigate when the click completed a text-selection gesture.
    const selectedText = window.getSelection?.()?.toString().trim();
    if (selectedText) return;

    const slug = feedCard.getAttribute('data-card-nav-slug');
    if (slug) window.location.href = `/db/${slug}/`;
  });
}

export function initMobileDrawers(evaluateCompatibility) {
  const filterTrigger = document.getElementById('sffMobileFilterTrigger');
  const telemetryTrigger = document.getElementById('sffMobileTelemetryTrigger');
  const closeFilterBtn = document.getElementById('sffDrawerCloseBtn');
  const closeTelemetryBtn = document.getElementById('sffTelemetryCloseBtn');
  const backdrop = document.getElementById('sffDrawerBackdrop');
  const inputsSidebar = document.getElementById('sffInputsSidebar') || document.querySelector('.sff-inputs');
  const telemetrySidebar = document.getElementById('sffTelemetrySidebar');

  const closeAllDrawers = (e) => {
    if (e) e.preventDefault();
    if (inputsSidebar) { deactivateAccessibleLayer(inputsSidebar); inputsSidebar.classList.remove('mobile-drawer-open'); inputsSidebar.setAttribute('aria-hidden', 'true'); }
    if (telemetrySidebar) { deactivateAccessibleLayer(telemetrySidebar); telemetrySidebar.classList.remove('mobile-drawer-open'); telemetrySidebar.setAttribute('aria-hidden', 'true'); }
    if (filterTrigger) { filterTrigger.classList.remove('active'); filterTrigger.setAttribute('aria-expanded', 'false'); }
    if (telemetryTrigger) { telemetryTrigger.classList.remove('active'); telemetryTrigger.setAttribute('aria-expanded', 'false'); }
    if (backdrop) { 
      backdrop.classList.remove('active'); 
      backdrop.setAttribute('aria-hidden', 'true'); 
    }
    document.body.style.overflow = '';
  };

  const openInputsDrawer = (e) => {
    if (e) e.preventDefault();
    if (inputsSidebar && inputsSidebar.classList.contains('mobile-drawer-open')) {
      closeAllDrawers();
      return;
    }
    if (telemetrySidebar) telemetrySidebar.classList.remove('mobile-drawer-open');
    if (inputsSidebar) {
      inputsSidebar.classList.add('mobile-drawer-open');
      inputsSidebar.setAttribute('aria-hidden', 'false');
      activateAccessibleLayer(inputsSidebar, { label: 'Compatibility filters', initialFocusSelector: '#sffDrawerCloseBtn', onClose: closeAllDrawers });
    }
    if (filterTrigger) { filterTrigger.classList.add('active'); filterTrigger.setAttribute('aria-expanded', 'true'); }
    if (telemetryTrigger) { telemetryTrigger.classList.remove('active'); telemetryTrigger.setAttribute('aria-expanded', 'false'); }
    if (backdrop) { 
      backdrop.classList.add('active'); 
      backdrop.setAttribute('aria-hidden', 'false'); 
    }
    document.body.style.overflow = 'hidden';
  };

  const openTelemetryDrawer = (e) => {
    if (e) e.preventDefault();
    if (telemetrySidebar && telemetrySidebar.classList.contains('mobile-drawer-open')) {
      closeAllDrawers();
      return;
    }
    if (inputsSidebar) inputsSidebar.classList.remove('mobile-drawer-open');
    if (telemetrySidebar) {
      telemetrySidebar.classList.add('mobile-drawer-open');
      telemetrySidebar.setAttribute('aria-hidden', 'false');
      activateAccessibleLayer(telemetrySidebar, { label: 'Compatibility telemetry', initialFocusSelector: '#sffTelemetryCloseBtn', onClose: closeAllDrawers });
    }
    if (telemetryTrigger) { telemetryTrigger.classList.add('active'); telemetryTrigger.setAttribute('aria-expanded', 'true'); }
    if (filterTrigger) { filterTrigger.classList.remove('active'); filterTrigger.setAttribute('aria-expanded', 'false'); }
    if (backdrop) { 
      backdrop.classList.add('active'); 
      backdrop.setAttribute('aria-hidden', 'false'); 
    }
    document.body.style.overflow = 'hidden';
  };

  if (filterTrigger) filterTrigger.addEventListener('click', openInputsDrawer);
  if (telemetryTrigger) telemetryTrigger.addEventListener('click', openTelemetryDrawer);
  if (closeFilterBtn) closeFilterBtn.addEventListener('click', closeAllDrawers);
  if (closeTelemetryBtn) closeTelemetryBtn.addEventListener('click', closeAllDrawers);
  if (backdrop) backdrop.addEventListener('click', closeAllDrawers);

  const mobileMedia = window.matchMedia('(max-width: 1024px)');
  let wasMobileState = mobileMedia.matches;

  const handleBreakpointChange = (e) => {
    if (e.matches !== wasMobileState) {
      wasMobileState = e.matches;
      if (!wasMobileState) closeAllDrawers();
      clearCardPool();
      cleanupOrphanNoteModals();
      if (evaluateCompatibility) evaluateCompatibility(true);
    }
  };

  if (mobileMedia.addEventListener) {
    mobileMedia.addEventListener('change', handleBreakpointChange);
  } else if (mobileMedia.addListener) {
    mobileMedia.addListener(handleBreakpointChange);
  }
}
